/**
 * Async browse + search entity picker for Havenlytics CPT posts.
 *
 * Opens with the first page of published posts (no typing required). Optional
 * search filters via the existing wp/v2 REST endpoint. IDs are stored
 * internally only — never shown in the UI.
 *
 * @package
 * @since   3.5.0
 */

import { __, sprintf } from '@wordpress/i18n';
import { useState, useEffect, useCallback, useRef } from '@wordpress/element';
import {
	Spinner,
	Button,
	BaseControl,
	SearchControl,
} from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Decode a common HTML entity string from REST title.rendered.
 *
 * @param {string} html Raw HTML title.
 * @return {string} Plain text.
 */
function decodeTitle( html ) {
	if ( ! html ) {
		return '';
	}
	const el = document.createElement( 'textarea' );
	el.innerHTML = html;
	return el.value;
}

/**
 * Human label when title/name is missing — never expose numeric IDs.
 *
 * @param {string} postType Post type slug.
 * @return {string} Fallback label.
 */
function untitledLabel( postType ) {
	return postType === 'hvnly_agent'
		? __( 'Untitled agent', 'havenlytics' )
		: __( 'Untitled property', 'havenlytics' );
}

/**
 * Coerce a value to a plain search string for REST. Never serialize functions.
 *
 * @param {*} value Candidate search value.
 * @return {string} Safe search string (may be empty).
 */
function toSearchString( value ) {
	return typeof value === 'string' ? value.trim() : '';
}

/**
 * Normalize a REST post into a picker item.
 *
 * @param {Object} post     REST post.
 * @param {string} postType Post type slug.
 * @return {Object} Normalized item.
 */
function mapPost( post, postType ) {
	const id = Number( post?.id ) || 0;
	const picker = post?.hvnly_picker && typeof post.hvnly_picker === 'object'
		? post.hvnly_picker
		: {};
	const fallback = untitledLabel( postType );

	if ( postType === 'hvnly_agent' ) {
		const name =
			picker.name ||
			decodeTitle( post?.title?.rendered ) ||
			fallback;
		return {
			id,
			label: name,
			kind: 'agent',
			name,
			avatar: picker.avatar || '',
			agency: picker.agency || '',
			propertyCount: Number( picker.property_count ) || 0,
			email: picker.email || '',
		};
	}

	const title =
		picker.title ||
		decodeTitle( post?.title?.rendered ) ||
		fallback;

	return {
		id,
		label: title,
		kind: 'property',
		title,
		thumbnail: picker.thumbnail || '',
		price: picker.price || '',
		status: picker.status || '',
		location: picker.location || '',
		mls: picker.mls || '',
	};
}

/**
 * @param {Object}   props
 * @param {string}   props.postType     REST post type slug (hvnly_property|hvnly_agent).
 * @param {string}   props.label        Control label.
 * @param {boolean}  [props.multiple]   Allow multiple selections.
 * @param {number|number[]} props.value Selected id(s).
 * @param {Function} props.onChange     Change handler.
 * @param {string}   [props.help]       Help text.
 * @param {string}   [props.placeholder] Search placeholder.
 * @return {Element} Control.
 */
export default function EntitySearchControl( {
	postType,
	label,
	multiple = false,
	value,
	onChange,
	help,
	placeholder,
} ) {
	const [ search, setSearch ] = useState( '' );
	const [ debouncedQuery, setDebouncedQuery ] = useState( '' );
	const [ items, setItems ] = useState( [] );
	const [ loading, setLoading ] = useState( false );
	const [ page, setPage ] = useState( 1 );
	const [ hasMore, setHasMore ] = useState( false );
	const [ selectedMap, setSelectedMap ] = useState( {} );
	const [ browsing, setBrowsing ] = useState( false );
	const listRef = useRef( null );
	const fetchGen = useRef( 0 );
	const queryRef = useRef( '' );

	const selectedIds = multiple
		? ( Array.isArray( value ) ? value.map( Number ).filter( Boolean ) : [] )
		: [];
	const singleId = ! multiple ? Number( value ) || 0 : 0;
	const singleSelected = singleId ? selectedMap[ String( singleId ) ] : null;

	// Browse list is always open for multi-select; for single, only while choosing.
	const showBrowse = multiple || browsing || ! singleId;

	// Debounce the search *string* — do not use @wordpress/compose useDebounce
	// here (that returns a debounced function, not a value).
	useEffect( () => {
		const next = toSearchString( search );
		const timer = window.setTimeout( () => {
			setDebouncedQuery( next );
		}, SEARCH_DEBOUNCE_MS );
		return () => window.clearTimeout( timer );
	}, [ search ] );

	const fetchPage = useCallback(
		async ( query, pageNum, append ) => {
			const searchQuery = toSearchString( query );
			queryRef.current = searchQuery;

			const gen = ++fetchGen.current;
			setLoading( true );
			try {
				const args = {
					per_page: PER_PAGE,
					page: pageNum,
					status: 'publish',
					orderby: searchQuery ? 'relevance' : 'title',
					order: 'asc',
					_fields: 'id,title,hvnly_picker',
				};
				// Only attach search when the user typed a string filter.
				if ( searchQuery ) {
					args.search = searchQuery;
				}

				const path = addQueryArgs( `/wp/v2/${ postType }`, args );
				const response = await apiFetch( {
					path,
					parse: false,
				} );
				if ( gen !== fetchGen.current ) {
					return;
				}
				const totalPages = parseInt(
					response.headers.get( 'X-WP-TotalPages' ) || '1',
					10
				);
				const data = await response.json();
				const mapped = ( Array.isArray( data ) ? data : [] ).map( ( post ) =>
					mapPost( post, postType )
				);

				setItems( ( prev ) => ( append ? [ ...prev, ...mapped ] : mapped ) );
				setHasMore( pageNum < totalPages );
				setPage( pageNum );

				setSelectedMap( ( prev ) => {
					const next = { ...prev };
					mapped.forEach( ( item ) => {
						next[ String( item.id ) ] = item;
					} );
					return next;
				} );
			} catch ( e ) {
				if ( gen !== fetchGen.current ) {
					return;
				}
				if ( ! append ) {
					setItems( [] );
				}
				setHasMore( false );
			} finally {
				if ( gen === fetchGen.current ) {
					setLoading( false );
				}
			}
		},
		[ postType ]
	);

	// Immediately load first page when the browse list is shown; re-fetch when
	// the debounced search *string* changes (empty = browse all published).
	useEffect( () => {
		if ( ! showBrowse ) {
			return;
		}
		fetchPage( debouncedQuery, 1, false );
	}, [ showBrowse, debouncedQuery, fetchPage ] );

	// Resolve labels for already-selected ids (edit load).
	useEffect( () => {
		const ids = multiple ? selectedIds : singleId ? [ singleId ] : [];
		const missing = ids.filter( ( id ) => ! selectedMap[ String( id ) ] );
		if ( ! missing.length ) {
			return;
		}
		let cancelled = false;
		( async () => {
			const next = { ...selectedMap };
			await Promise.all(
				missing.map( async ( id ) => {
					try {
						const post = await apiFetch( {
							path: addQueryArgs( `/wp/v2/${ postType }/${ id }`, {
								_fields: 'id,title,hvnly_picker',
							} ),
						} );
						next[ String( id ) ] = mapPost( post, postType );
					} catch ( e ) {
						const fallback = untitledLabel( postType );
						next[ String( id ) ] = {
							id,
							label: fallback,
							kind: postType === 'hvnly_agent' ? 'agent' : 'property',
							title: fallback,
							name: fallback,
						};
					}
				} )
			);
			if ( ! cancelled ) {
				setSelectedMap( next );
			}
		} )();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ multiple ? selectedIds.join( ',' ) : singleId, postType ] );

	// Infinite scroll: load next page near the bottom of the list.
	useEffect( () => {
		if ( ! showBrowse || ! hasMore || loading ) {
			return undefined;
		}
		const el = listRef.current;
		if ( ! el ) {
			return undefined;
		}
		const onScroll = () => {
			if ( el.scrollTop + el.clientHeight >= el.scrollHeight - 48 ) {
				fetchPage( queryRef.current, page + 1, true );
			}
		};
		el.addEventListener( 'scroll', onScroll );
		return () => el.removeEventListener( 'scroll', onScroll );
	}, [ showBrowse, hasMore, loading, page, fetchPage ] );

	const toggleItem = ( item ) => {
		const id = Number( item?.id ) || 0;
		if ( ! id ) {
			return;
		}
		setSelectedMap( ( prev ) => ( { ...prev, [ String( id ) ]: item } ) );
		if ( multiple ) {
			if ( selectedIds.includes( id ) ) {
				onChange( selectedIds.filter( ( x ) => x !== id ) );
			} else {
				onChange( [ ...selectedIds, id ] );
			}
			return;
		}
		onChange( id );
		setBrowsing( false );
		setSearch( '' );
	};

	const removeId = ( id ) => {
		if ( multiple ) {
			onChange( selectedIds.filter( ( x ) => x !== id ) );
		} else {
			onChange( 0 );
			setBrowsing( true );
		}
	};

	const startChange = () => {
		setBrowsing( true );
		setSearch( '' );
	};

	const searchPlaceholder =
		placeholder ||
		( postType === 'hvnly_agent'
			? __( 'Filter agents (optional)…', 'havenlytics' )
			: __( 'Filter by title, address, or MLS (optional)…', 'havenlytics' ) );

	const emptyLabel = debouncedQuery
		? __( 'No matches found.', 'havenlytics' )
		: postType === 'hvnly_agent'
			? __( 'No published agents found.', 'havenlytics' )
			: __( 'No published properties found.', 'havenlytics' );

	const loadingLabel = debouncedQuery
		? __( 'Searching…', 'havenlytics' )
		: __( 'Loading…', 'havenlytics' );

	const renderPropertyRow = ( item ) => (
		<div className="hvnly-entity-search__row hvnly-entity-search__row--property">
			{ item.thumbnail ? (
				<img
					className="hvnly-entity-search__thumb"
					src={ item.thumbnail }
					alt=""
				/>
			) : (
				<span className="hvnly-entity-search__thumb hvnly-entity-search__thumb--empty" />
			) }
			<div className="hvnly-entity-search__meta">
				<strong className="hvnly-entity-search__title">{ item.title }</strong>
				{ item.location ? (
					<span className="hvnly-entity-search__line">{ item.location }</span>
				) : null }
				<span className="hvnly-entity-search__line hvnly-entity-search__line--muted">
					{ [ item.price, item.status ].filter( Boolean ).join( ' · ' ) }
				</span>
			</div>
		</div>
	);

	const renderAgentRow = ( item ) => (
		<div className="hvnly-entity-search__row hvnly-entity-search__row--agent">
			{ item.avatar ? (
				<img
					className="hvnly-entity-search__avatar"
					src={ item.avatar }
					alt=""
				/>
			) : (
				<span className="hvnly-entity-search__avatar hvnly-entity-search__avatar--empty" />
			) }
			<div className="hvnly-entity-search__meta">
				<strong className="hvnly-entity-search__title">{ item.name }</strong>
				{ item.agency ? (
					<span className="hvnly-entity-search__line">{ item.agency }</span>
				) : null }
				<span className="hvnly-entity-search__line hvnly-entity-search__line--muted">
					{ [
						item.propertyCount > 0
							? sprintf(
								/* translators: %d: listing count */
								__( '%d Listings', 'havenlytics' ),
								item.propertyCount
							)
							: '',
						item.email || '',
					]
						.filter( Boolean )
						.join( ' · ' ) }
				</span>
			</div>
		</div>
	);

	const renderItem = ( item ) =>
		item.kind === 'agent' ? renderAgentRow( item ) : renderPropertyRow( item );

	return (
		<div className="hvnly-entity-search">
			<BaseControl
				label={ label }
				help={ help }
				__nextHasNoMarginBottom
			>
				{ ! multiple && singleSelected && ! browsing ? (
					<div className="hvnly-entity-search__chosen">
						{ renderItem( singleSelected ) }
						<Button
							size="small"
							variant="tertiary"
							onClick={ startChange }
						>
							{ __( 'Change', 'havenlytics' ) }
						</Button>
					</div>
				) : (
					<>
						<SearchControl
							label={ label }
							hideLabelFromVision
							value={ search }
							onChange={ ( next ) =>
								setSearch( typeof next === 'string' ? next : '' )
							}
							placeholder={ searchPlaceholder }
							__nextHasNoMarginBottom
						/>
						<div
							className="hvnly-entity-search__dropdown"
							role="listbox"
							ref={ listRef }
						>
							{ loading && items.length === 0 && (
								<p className="hvnly-entity-search__status">
									<Spinner /> { loadingLabel }
								</p>
							) }
							{ ! loading && items.length === 0 && (
								<p className="hvnly-entity-search__status">
									{ emptyLabel }
								</p>
							) }
							<ul className="hvnly-entity-search__results">
								{ items.map( ( item ) => {
									const selected = multiple
										? selectedIds.includes( item.id )
										: singleId === item.id;
									return (
										<li key={ item.id }>
											<button
												type="button"
												className={
													'hvnly-entity-search__option' +
													( selected ? ' is-selected' : '' )
												}
												aria-selected={ selected }
												onClick={ () => toggleItem( item ) }
											>
												{ multiple && (
													<span
														className="hvnly-entity-search__check"
														aria-hidden="true"
													>
														{ selected ? '✓' : '' }
													</span>
												) }
												{ renderItem( item ) }
											</button>
										</li>
									);
								} ) }
							</ul>
							{ loading && items.length > 0 && (
								<p className="hvnly-entity-search__status">
									<Spinner />
								</p>
							) }
							{ hasMore && ! loading && (
								<div className="hvnly-entity-search__more">
									<Button
										variant="secondary"
										onClick={ () =>
											fetchPage( queryRef.current, page + 1, true )
										}
									>
										{ __( 'Load more', 'havenlytics' ) }
									</Button>
								</div>
							) }
						</div>
						{ ! multiple && browsing && singleId > 0 && (
							<Button
								size="small"
								variant="link"
								onClick={ () => {
									setBrowsing( false );
									setSearch( '' );
								} }
							>
								{ __( 'Cancel', 'havenlytics' ) }
							</Button>
						) }
					</>
				) }
			</BaseControl>

			{ multiple && selectedIds.length > 0 && (
				<ul className="hvnly-entity-search__chips">
					{ selectedIds.map( ( id ) => {
						const item = selectedMap[ String( id ) ];
						const chipLabel =
							item?.label || untitledLabel( postType );
						return (
							<li key={ id }>
								<span className="hvnly-entity-search__chip-check" aria-hidden="true">
									✓
								</span>
								<span className="hvnly-entity-search__chip-label">
									{ chipLabel }
								</span>
								<Button
									size="small"
									variant="tertiary"
									className="hvnly-entity-search__chip-remove"
									onClick={ () => removeId( id ) }
									aria-label={ __( 'Remove', 'havenlytics' ) }
								>
									×
								</Button>
							</li>
						);
					} ) }
				</ul>
			) }
		</div>
	);
}
